import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

/**
 * A code (a task's B2C-044, a workstream's F) that copies itself on click. Stops the click so it never
 * opens the row it sits in.
 */
export function CopyCode({ code, className }: { code: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    []
  );

  return (
    <button
      type="button"
      title={copied ? "Copied" : `Copy ${code}`}
      aria-label={copied ? `${code} copied` : `Copy ${code}`}
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard
          ?.writeText(code)
          .then(() => {
            setCopied(true);
            if (timer.current !== null) window.clearTimeout(timer.current);
            timer.current = window.setTimeout(() => setCopied(false), 1200);
          })
          .catch(() => {});
      }}
      className={cn(
        "font-data group/code hover:bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded-[4px] px-1 py-0.5 text-[11px] tabular-nums transition-colors",
        className,
        // after className, so the "copied" tick colour wins over a caller's colour
        copied && "text-[var(--rag-green)]"
      )}
    >
      {code}
      {copied ? (
        <Check className="size-3" aria-hidden />
      ) : (
        <Copy
          className="size-3 opacity-0 transition-opacity group-hover/code:opacity-100"
          aria-hidden
        />
      )}
    </button>
  );
}
